import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useI18n } from '../../hooks/useI18n';
import {
  Toolbar, Typography, Chip, Box, Link, IconButton, Menu, MenuItem, Stack, Avatar, Divider, ListItemIcon, ListItemText
} from "@mui/material";
import NotificationMenu from "../../components/NotificationMenu";
import LanguageSelector from "../../components/LanguageSelector";
import { getInitials } from "../../utils";
import { setThemeMode } from "../../store/themeSlice";

// Import icone Material-UI
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import BuildIcon from '@mui/icons-material/Build';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { HelpOutline } from "@mui/icons-material";
import { canAccessSettings, canAccessToolbox } from '../../utils/permissions';




const NavItem = ({ to, icon, label, onClick, type=null, disabled=false, disabledMsg=null }) => { 
  const NavIcon = icon;
  const TYPES = {
    external: {
      component: 'a',
      props: { target: "_blank", rel: "noopener noreferrer" },
      textColor: 'text.primary',
    },
    logout: {
      iconColor: 'error',
      textColor: 'error.main',
    },
    theme: {
      iconColor: 'primary',
      textColor: 'text.primary',
    }
  }
  
  const iconColor = TYPES[type]?.iconColor || 'inherit';
  const textColor = TYPES[type]?.textColor || 'text.primary';

  return (
    <MenuItem 
        onClick={onClick}
        component={type === 'external' ? 'a' : RouterLink}
        to={to}
        {...(type === 'external' ? { target: "_blank", rel: "noopener noreferrer", href: to } : {})}
        disabled={disabled}
        sx={{ ":hover": { borderRadius: 1 } }}
      >
        <ListItemIcon><NavIcon fontSize="small" color={iconColor} /></ListItemIcon>
        <ListItemText primary={label} secondary={disabled ? disabledMsg : null} slotProps={{
            "primary": { variant: 'body2', color: textColor },
            "secondary": { variant: 'caption', color: 'error.main' }
        }
        }/>
      </MenuItem>
)};

const ThemeMenuItem = ({ mode, currentMode, label, icon, onClick }) => {
  const ThemeIcon = icon;
  return (
    <MenuItem 
      onClick={onClick}
      sx={{ ":hover": { borderRadius: 1 } }}
    >
      <ListItemIcon>
        <ThemeIcon fontSize="small" color={currentMode === mode ? 'primary' : 'inherit'} />
      </ListItemIcon>
      <ListItemText primary={label}
        slotProps={{
          "primary": { variant: 'body2', color: currentMode === mode ? 'primary.main' : 'text.primary' }
        }}
      />
      
    </MenuItem>
  )
};

export default function Nav() {
  const { t } = useI18n('common', 'features');
  const user = useSelector((state) => state.user.user);
  const themeMode = useSelector((state) => state.theme.mode);
  const themeSwitcherEnabled = useSelector((state) => state.features.themeSwitcherEnabled);
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleThemeChange = (mode) => {
    dispatch(setThemeMode(mode));
    handleClose();
  };

  return (
    <Toolbar disableGutters>
      <Box sx={{ flexGrow: 1 }}>
        <Link component={RouterLink} to="/" underline="none">
          <Typography
            variant="h6"
            component="div"
            fontWeight={700}
            color="text.primary"
            sx={{
              "& strong": {
                color: "primary.main",
              },
            }}
          >
            {t('app.title', { defaultValue: 'C@rtina' })} <Chip label={t('app.module', { defaultValue: 'FINANCE'})} color="secondary" size="small" />
          </Typography>
        </Link>
      </Box>

      <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
        <NotificationMenu />
        <LanguageSelector />

        <IconButton onClick={handleOpen} size="small">
          <Avatar sx={{ width: 34, height: 34, bgcolor: 'secondary.main', color:'secondary.contrastText', fontWeight: 'bold', fontSize: '1rem' }}>
            {getInitials(user)}
          </Avatar>
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ sx: { mt: 1.5, minWidth: 200, borderRadius: 2, py: 0.5, px: 2 } }}
        >
          {/* Username */}
          <MenuItem disabled sx={{ justifyContent: 'center', pointerEvents: 'none', opacity: 1 }}>
            <Typography fontWeight='bold' color="text.secondary">{user?.username}</Typography>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          {/* Home Button */}
          <NavItem to="/"
            icon={HomeIcon}
            label={t('nav.home', { defaultValue: 'Home' })}
            onClick={handleClose} 
          />
          {/* Settings Button - only for admin and director */}
          {user && canAccessSettings(user) && (
            <NavItem
              to="/settings"
              icon={SettingsIcon}
              label={t('nav.settings', { defaultValue: 'Impostazioni' })}
              onClick={handleClose}
            />
          )}

          {/* Toolbox - only for admin */}
          {user && canAccessToolbox(user) && (
            <NavItem
              to="/api/toolbox"
              icon={BuildIcon}
              label={t('nav.toolbox', { defaultValue: 'Toolbox' })}
              onClick={handleClose}
              type="external"
            />
          )}

          {/* Profile Button */}
          <NavItem
            to="/profile"
            icon={PersonIcon}
            label={t('nav.profile', { defaultValue: 'Profilo' })}
            onClick={handleClose}
          />

          {themeSwitcherEnabled && (
            <Box>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem disabled sx={{ justifyContent: 'center', pointerEvents: 'none', opacity: 1 }}>
                <Typography variant='body2' fontWeight='bold'>{t('nav.theme', { defaultValue: 'Tema'})}</Typography>
              </MenuItem>
              <ThemeMenuItem
                mode="light"
                currentMode={themeMode}
                label={t('features:theme.lightMode', { defaultValue: 'Chiaro' })}
                icon={Brightness7Icon}
                onClick={() => handleThemeChange('light')}
              />

              <ThemeMenuItem
                mode="dark"
                currentMode={themeMode}
                label={t('features:theme.darkMode', { defaultValue: 'Scuro' })}
                icon={Brightness4Icon}
                onClick={() => handleThemeChange('dark')}
              />
              <ThemeMenuItem
                mode="system"
                currentMode={themeMode}
                label={t('features:theme.autoMode', { defaultValue: 'Sistema' })}
                icon={SettingsBrightnessIcon}
                onClick={() => handleThemeChange('system')}
              />
            </Box>
          )}

          <Divider sx={{ my: 0.5 }} />

          <NavItem
            to="/help"
            icon={HelpOutline}
            label={t('nav.help', { defaultValue: 'Assistenza' })}
            onClick={handleClose}
            disabled={false}
          />

          <NavItem
            onClick={handleClose}
            to="/logout"
            icon={LogoutIcon}
            type="logout"
            label={t('nav.logout', { defaultValue: 'Logout' })}
          />
        </Menu>
      </Stack>
    </Toolbar>
  );
}
